//Exercise service (daily exercise logic and completion tracking)
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  getDoc,
  orderBy,
  limit,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './firebase';
import { DailyExercise, ExerciseCompletion, User, Forest } from '../types';
import { GAME_CONFIG } from '../utils/gameConfig';



//Exercise Availability logic

export const getCurrentExerciseDay = (forest: Forest): number => {
  const now = new Date();
  const gameStartDate = forest.startDate;
  
  const timeDiff = now.getTime() - gameStartDate.getTime();
  
  //duration based on mode
  let dayDuration: number;
  if (forest.isQuickGame) {
    dayDuration = 60 * 1000;
  } else {
    dayDuration = 24 * 60 * 60 * 1000;
  }
  
  const daysSinceStart = Math.floor(timeDiff / dayDuration);
  const currentDay = Math.max(1, daysSinceStart + 1);
  
  return Math.min(currentDay, forest.gameDuration);
};

export const isExerciseAvailable = (exerciseDay: number, forest: Forest): boolean => {
  const currentDay = getCurrentExerciseDay(forest);
  return exerciseDay <= currentDay;
};

export const hasUserCompletedExercise = (user: User, exerciseDay: number): boolean => {
  return user.completedExercises?.includes(exerciseDay) || false;
};

export const canUserDoExercise = (user: User, exerciseDay: number, forest: Forest): boolean => {
  //Check if exercise is available for this day
  if (!isExerciseAvailable(exerciseDay, forest)) {
    return false;
  }
  
  //Check if user has already completed this exercise
  if (hasUserCompletedExercise(user, exerciseDay)) {
    return false;
  }
  
  return true;
};



//get Execise

export const getTodaysExercise = async (forest: Forest): Promise<DailyExercise | null> => {
  try {
    const currentDay = getCurrentExerciseDay(forest);
    return await getExerciseForDay(forest.dailyExerciseGroupId, currentDay);
  } catch (error) {
    console.error('Error getting today\'s exercise:', error);
    return null;
  }
};

export const getExerciseForDay = async (groupId: string, day: number): Promise<DailyExercise | null> => {
  try {
    const exercisesRef = collection(db, 'dailyExercises');
    const q = query(
      exercisesRef,
      where('groupId', '==', groupId),
      where('day', '==', day),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as DailyExercise;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting exercise for day:', error);
    return null;
  }
};

export const getAvailableExercises = async (
  forest: Forest, 
  user: User
): Promise<{ exercise: DailyExercise; day: number; available: boolean; completed: boolean }[]> => {
  try {
    const exercisesRef = collection(db, 'dailyExercises');
    const q = query(
      exercisesRef,
      where('groupId', '==', forest.dailyExerciseGroupId),
      orderBy('day', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    const currentDay = getCurrentExerciseDay(forest);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      const exercise = {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as DailyExercise;
      
      return {
        exercise,
        day: exercise.day,
        available: isExerciseAvailable(exercise.day, forest),
        completed: hasUserCompletedExercise(user, exercise.day)
      };
    });
  } catch (error) {
    console.error('Error getting available exercises:', error);
    return [];
  }
};



//Exercise Completion Tracking

export const completeExercise = async (
  userId: string,
  forestId: string,
  exercise: DailyExercise,
  seedsToAward: number = GAME_CONFIG.SEED_PER_DAILY_EXERCISE
): Promise<void> => {
  try {
    console.log(`Completing exercise Day ${exercise.day} for user ${userId}`);
    
    //Record the completion
    const completionData: Omit<ExerciseCompletion, 'id'> = {
      userId,
      forestId,
      exerciseId: exercise.id,
      exerciseDay: exercise.day,
      completedAt: new Date(),
      seedsEarned: seedsToAward,
      createdAt: new Date(),
    };
    
    const completionsRef = collection(db, 'forests', forestId, 'exerciseCompletions');
    await addDoc(completionsRef, {
      ...completionData,
      completedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    });
    
    console.log(`Exercise completion recorded for Day ${exercise.day}`);
    
  } catch (error) {
    console.error('Error completing exercise:', error);
    throw error;
  }
};

export const updateUserExerciseCompletion = async (
  userId: string,
  exerciseDay: number,
  seedsToAward: number = GAME_CONFIG.SEED_PER_DAILY_EXERCISE
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    
    //Get current user data
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userDoc.data() as User;
    const completedExercises = userData.completedExercises || [];
    
    //Add this exercise day to completed list if not already there
    const updatedCompletedExercises = completedExercises.includes(exerciseDay) 
      ? completedExercises 
      : [...completedExercises, exerciseDay].sort();
    
    //Update user (w/ new seeds and completion status)
    await updateDoc(userRef, {
      seeds: (userData.seeds || 0) + seedsToAward,
      totalSeedsEarned: (userData.totalSeedsEarned || 0) + seedsToAward,
      completedExercises: updatedCompletedExercises,
      lastExerciseDate: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    console.log(`User updated: +${seedsToAward} seeds, exercise ${exerciseDay} marked complete`);
    
  } catch (error) {
    console.error('Error updating user exercise completion:', error);
    throw error;
  }
};


//Exercise stats and analytics

export const getUserExerciseStats = async (userId: string, forestId: string) => {
  try {
    const completionsRef = collection(db, 'forests', forestId, 'exerciseCompletions');
    const q = query(
      completionsRef,
      where('userId', '==', userId),
      orderBy('completedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const completions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      completedAt: doc.data().completedAt.toDate(),
      createdAt: doc.data().createdAt.toDate(),
    })) as ExerciseCompletion[];
    
    return {
      totalCompleted: completions.length,
      totalSeedsFromExercises: completions.reduce((sum, c) => sum + c.seedsEarned, 0),
      completedDays: completions.map(c => c.exerciseDay).sort(),
      lastCompleted: completions[0]?.completedAt || null,
      completions
    };
  } catch (error) {
    console.error('Error getting user exercise stats:', error);
    return {
      totalCompleted: 0,
      totalSeedsFromExercises: 0,
      completedDays: [],
      lastCompleted: null,
      completions: []
    };
  }
};

export const getForestExerciseStats = async (forestId: string) => {
  try {
    const completionsRef = collection(db, 'forests', forestId, 'exerciseCompletions');
    const querySnapshot = await getDocs(completionsRef);
    
    const completions = querySnapshot.docs.map(doc => doc.data()) as Omit<ExerciseCompletion, 'id'>[];
    
    //Group by day
    const completionsByDay: Record<number, number> = {};
    completions.forEach(completion => {
      completionsByDay[completion.exerciseDay] = (completionsByDay[completion.exerciseDay] || 0) + 1;
    });
    
    //Group by user
    const completionsByUser: Record<string, number> = {};
    completions.forEach(completion => {
      completionsByUser[completion.userId] = (completionsByUser[completion.userId] || 0) + 1;
    });
    
    return {
      totalCompletions: completions.length,
      completionsByDay,
      completionsByUser,
      uniqueUsers: Object.keys(completionsByUser).length,
      averageCompletionsPerUser: Object.keys(completionsByUser).length > 0 
        ? completions.length / Object.keys(completionsByUser).length 
        : 0
    };
  } catch (error) {
    console.error('Error getting forest exercise stats:', error);
    return {
      totalCompletions: 0,
      completionsByDay: {},
      completionsByUser: {},
      uniqueUsers: 0,
      averageCompletionsPerUser: 0
    };
  }
};


//Helper Functions

export const getExerciseButtonState = (user: User, forest: Forest) => {
  const currentDay = getCurrentExerciseDay(forest);
  const canDoToday = canUserDoExercise(user, currentDay, forest);
  const hasCompletedToday = hasUserCompletedExercise(user, currentDay);
  
  return {
    currentDay,
    available: isExerciseAvailable(currentDay, forest),
    canDo: canDoToday,
    completed: hasCompletedToday,
    isDisabled: !canDoToday,
    buttonText: hasCompletedToday ? `Day ${currentDay} ✓` : `Day ${currentDay}`,
    tooltipText: hasCompletedToday 
      ? `Exercise Day ${currentDay} completed!`
      : canDoToday 
        ? `Click to start Day ${currentDay} exercise`
        : currentDay > forest.gameDuration
          ? 'All exercises completed!'
          : 'Exercise not yet available'
  };
};